import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from "classnames";
import ProgressBar from 'react-bootstrap/ProgressBar';

import { useDispatch } from "react-redux";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ViewCourseContent } from "./content";

import { AssessmentView } from "./content/assessment/view";
import { completeCourse, invalidateCompleteCourse, updateCourseSession } from "src/redux/actions/coaching/learners";
import { LearningRecordCourse } from "src/types/coaching/learners";
import Button from "src/components/button";
import { CourseContext } from "../context";
import { ElementTimerComponent } from "src/components/elementTimer";


import styles from "./structure.module.scss";
import Tooltip from "src/components/tooltip";
import Text from "src/components/text";
import { AssessmentCard, LessonCard, PodcastCard, SectionCard, SimulationCard } from "../components/cards";
import { flattenContents, getIndexForContent } from "src/pages/coaching/manage/courses/edit/helper";
import Slider from "src/components/slider";

interface CourseStructureComponentProps {
  course: LearningRecordCourse;
}

export const CourseStructureComponent: React.FC<CourseStructureComponentProps> = ({ course }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get("index") || "0");
  }, [searchParams]);

  const childContentIndex = useMemo(() => {
    return parseInt(searchParams.get("child") || "0");
  }, [searchParams]);


  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hideToggle, setHideToggle] = useState(false);
  const [showStartSimulationSlider, setShowStartSimulationSlider] = useState(false);

  const courseContext = React.useContext(CourseContext);

  const durationRef = React.useRef<any>();

  const contents = useMemo(() => course?.course?.contents || [], [course]);
  const status = useMemo(() => _.get(course, "status"), [course]);
  const notStarted = _.includes(["NOT_STARTED", "NOT_ENROLLED"], status);
  const contentSessions = courseContext.getContentSessions?.() || [];

  const handleBackClicked = () => {
    if (courseContext.onBack) {
      courseContext.onBack();
    }
  }

  const handleContentClicked = (index: number, section?: number | null) => {
    if (courseContext.onContentClicked) {
      courseContext.onContentClicked(index, section);
    }
  }

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSimulatorModeChange = (isSimulator: boolean) => {
    setSidebarCollapsed(isSimulator);
    setHideToggle(isSimulator);
  };

  const handleEndCourse = () => {
    const { change } = durationRef.current.duration();

    dispatch(completeCourse({
      id: course.id!,
      duration: change,
    }));
  }

  const handleDurationChange = (time: number, change: number) => {
    if (course.status !== 'COMPLETED') {
      console.log(`Duration changed to ${time} seconds. Change: ${change}`);
      dispatch(updateCourseSession({
        id: course.id,
        duration: time,
      }));
    }
  }

  const progressMeta = useMemo(() => {
    const sessions = contentSessions;
    const flattenedContents = flattenContents(contents || []).filter(c => c.entityType === 'lesson' || c.entityType === 'assessment' || c.entityType === 'podcast' || c.entityType === 'simulation');

    const total = (flattenedContents || []).length;

    if (total === 0) return { total: 0, completed: 0, remaining: 0, progress: 0 };

    const completed = (flattenedContents || []).reduce((acc, c) => {
      const session = sessions.find(s => s.id === c.id);
      if (!session) return acc;

      if (c.entityType === "lesson") {
        return acc + 1;
      }

      if (c.entityType === "assessment" && session.status === "COMPLETED") {
        return acc + 1;
      }

      if (c.entityType === "podcast" && session.status === "COMPLETED") {
        return acc + 1;
      }

      if (c.entityType === "simulation" && session.status === "COMPLETED" &&
        _.get(session, "report.status") !== "in_sufficient_duration"
      ) {
        return acc + 1;
      }

      return acc;
    }, 0);

    const progress = (completed / total) * 100;

    return {
      total,
      completed,
      remaining: total - completed,
      progress: parseFloat(progress.toFixed(2))
    };
  }, [contents, contentSessions]);

  if (courseContext.completedCourse) {
    return (
      <CourseCompletionDialog course={course} />
    )
  }

  if (courseContext.activeAssessment) {
    return (
      <AssessmentView
        course={course}
        assessment={courseContext.activeAssessment}
      />
    )
  }

  if (notStarted) {
    return <Navigate to={`/coaching/courses/${course.id}`} />
  }

  return (
    <ElementTimerComponent id={course.id!} ref={durationRef} onDurationChange={handleDurationChange}>
      <div className={cx(styles.editContainer, { [styles.sidebarCollapsed]: sidebarCollapsed })}>
        {!hideToggle && (
          <Tooltip content="Toggle sidebar" placement="right">
            <button className={styles.toggleButton} onClick={toggleSidebar}>
              <i className={`paperflite-side-panel`}></i>
            </button>
          </Tooltip>
        )}
        <div className={styles.left}>
          <div className={styles.content}>
            <div className={styles.header}>
              <button
                className={styles.backButton}
                onClick={handleBackClicked}
              >
                <i className={"paperflite-left-arrow"}></i>
                <Text className={styles.courseName} text={course?.course?.name || ""} lines={1} tooltip={{ placement: 'bottom' }} />
              </button>
              <div className={styles.titleBar}>
                <h2 className={styles.title}>Course Structure</h2>
                <div className={styles.progressWrapper}>
                  <ProgressBar now={progressMeta.progress} />
                  <span className={styles.progressLabel}>
                    {progressMeta.completed} / {progressMeta.total}
                  </span>
                </div>
              </div>
            </div>
            <div className={styles.contentsList}>
              {contents.map((content, index) => {
                const type = _.get(content, "entityType");
                const contentDisplayIndex = getIndexForContent(contents, content.uuid, content.parent);

                return (
                  <React.Fragment key={content.uuid}>
                    {type === "lesson" && (
                      <LessonCard
                        className={styles.contentCard}
                        lessonIndex={contentDisplayIndex}
                        course={course}
                        lesson={content}
                        index={index}
                        active={index === contentIndex}
                        disabled={notStarted}
                        onClick={handleContentClicked}
                      />
                    )}

                    {type === "assessment" && (
                      <AssessmentCard
                        className={styles.contentCard}
                        assessmentIndex={contentDisplayIndex}
                        course={course}
                        assessment={content}
                        index={index}
                        active={index === contentIndex}
                        disabled={notStarted}
                        onClick={handleContentClicked}
                      />
                    )}

                    {type === "section" && (
                      <SectionCard
                        className={styles.contentCard}
                        course={course}
                        section={content}
                        sectionIndex={contentDisplayIndex}
                        contentChildIndex={index === contentIndex ? childContentIndex : -1}
                        index={index}
                        active={index === contentIndex}
                        disabled={notStarted}
                        onClick={handleContentClicked}
                      />
                    )}

                    {type === "podcast" && (
                      <PodcastCard
                        className={styles.contentCard}
                        podcastIndex={contentDisplayIndex}
                        course={course}
                        podcast={content}
                        index={index}
                        active={index === contentIndex}
                        disabled={notStarted}
                        onClick={handleContentClicked}
                      />
                    )}

                    {type === "simulation" && (
                      <SimulationCard
                        className={styles.contentCard}
                        simulationIndex={contentDisplayIndex}
                        course={course}
                        simulation={content}
                        index={index}
                        active={index === contentIndex}
                        disabled={notStarted}
                        onClick={handleContentClicked}
                      />
                    )}
                    <div className={styles.dividerLine}></div>
                  </React.Fragment>
                );
              })}

              {course.status !== 'COMPLETED' && (
                <div className={styles.footer}>
                  <Button variant="primary" className={styles.completeButton}
                    icon="paperflite-tick"
                    text={courseContext.completingCourse ? 'Ending...' : 'End course'}
                    disabled={courseContext.completingCourse || !courseContext.canCompleteCourse}
                    onClick={() => handleEndCourse()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.right}>
          <ViewCourseContent
            course={course}
            collapsed={sidebarCollapsed}
            onSimulatorModeChange={handleSimulatorModeChange}
          />
        </div>
      </div>
    </ElementTimerComponent>
  );
};


interface CourseCompletionDialogProps {
  course: LearningRecordCourse;
}

const CourseCompletionDialog: React.FC<CourseCompletionDialogProps> = ({ course }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleExit = () => {
    navigate(`/coaching/courses`);
  }

  useEffect(() => {
    return () => {
      dispatch(invalidateCompleteCourse(course.id));
    }
  }, []);

  return (
    <div className={cx(styles.completeDialog)}>
      <div className={styles.body}>
        <img src="/images/coaching/complete_course.svg" alt="Course completed" />
        <h3>Congratulations! <br></br>you’ve successfully completed the course</h3>
        <div className={styles.actions}>
          <Button variant="secondary" icon="paperflite-close" text="Exit" onClick={handleExit} />
        </div>
      </div>
    </div>
  );
}